import { Controller } from "@hotwired/stimulus"
import Chart from "chart.js/auto"

// Diverging colors of the ratings 1 (not helpful) to 5 (very helpful): red and
// blue arms around a gray midpoint. The dark theme mirrors the lightness, so
// the ends stay the most prominent. Every step clears 2:1 against the surface.
// Keep in sync with .ai-feedback-rating-* in _ai.scss.
const RATING_COLORS = {
  light: ["#b13f3c", "#ef9790", "#b8b7b4", "#86b6ef", "#256abf"],
  dark: ["#ef9790", "#b13f3c", "#5e5d5b", "#256abf", "#86b6ef"]
}

// Categorical colors of the usage (generated, cached), validated for color
// vision deficiencies on the light and dark surface.
// Keep in sync with $ai-usage-series-colors in _ai.scss.
const SERIES_COLORS = {
  light: ["#2a78d6", "#eb6834"],
  dark: ["#3987e5", "#d95926"]
}

// Bar charts of the ratings of the AI summaries (admin/ai_summary_stats):
// one bar per rating (a dataset with `ratings`), or the ratings per period
// stacked (one dataset per `rating`). The usage per period is stacked too, in
// categorical colors (one dataset per `series`).
export default class extends Controller {
  static targets = ["canvas"]

  static values = {
    labels: Array,
    datasets: Array,
    stacked: Boolean
  }

  connect() {
    const stacked = this.stackedValue

    this.chart = new Chart(this.canvasTarget, {
      type: "bar",
      data: {
        labels: this.labelsValue,
        datasets: this.datasetsValue.map((dataset) => ({
          ...dataset,
          // Stacked segments are separated by a gap in the surface color,
          // single bars get rounded ends.
          borderWidth: stacked ? { top: 2 } : 0,
          borderRadius: stacked ? 0 : 4,
          maxBarThickness: 48
        }))
      },
      options: {
        maintainAspectRatio: false,
        animation: false,
        interaction: {
          mode: "index",
          intersect: false
        },
        plugins: {
          // A single series is named by the title of the chart
          legend: {
            display: stacked,
            position: "top",
            align: "start",
            labels: { boxWidth: 12 }
          },
          tooltip: {
            callbacks: {
              label: (context) => this.tooltipLabel(context)
            }
          }
        },
        scales: {
          x: {
            stacked: stacked,
            grid: { display: false },
            // All ratings are labeled, the days may be skipped
            ticks: { maxRotation: 0, autoSkip: stacked, autoSkipPadding: 16 }
          },
          y: {
            stacked: stacked,
            beginAtZero: true,
            ticks: {
              precision: 0,
              callback: (value) => value.toLocaleString("de-DE")
            }
          }
        }
      }
    })

    this.applyTheme()

    // The color mode switcher sets data-bs-theme on <html>
    this.themeObserver = new MutationObserver(() => this.applyTheme())
    this.themeObserver.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-bs-theme"]
    })
  }

  disconnect() {
    this.themeObserver?.disconnect()
    this.chart?.destroy()
  }

  // Count and share of the bar (single bars) or of the period (stacked).
  tooltipLabel(context) {
    const count = context.parsed.y
    const total = this.stackedValue
      ? context.chart.data.datasets.reduce((sum, dataset) => sum + dataset.data[context.dataIndex], 0)
      : context.dataset.data.reduce((sum, value) => sum + value, 0)
    const share = total > 0 ? ` (${Math.round((count / total) * 100)} %)` : ""

    return `${context.dataset.label}: ${count.toLocaleString("de-DE")}${share}`
  }

  applyTheme() {
    const theme = document.documentElement.getAttribute("data-bs-theme") === "dark" ? "dark" : "light"
    const style = getComputedStyle(document.documentElement)
    const textColor = style.getPropertyValue("--bs-secondary-color").trim()
    const gridColor = style.getPropertyValue("--bs-border-color-translucent").trim()
    const surfaceColor = getComputedStyle(this.element).backgroundColor !== "rgba(0, 0, 0, 0)"
      ? getComputedStyle(this.element).backgroundColor
      : style.getPropertyValue("--bs-body-bg").trim()
    const colors = RATING_COLORS[theme]

    this.chart.data.datasets.forEach((dataset) => {
      if (dataset.series) {
        dataset.backgroundColor = SERIES_COLORS[theme][dataset.series - 1]
      } else {
        dataset.backgroundColor = dataset.ratings
          ? dataset.ratings.map((rating) => colors[rating - 1])
          : colors[dataset.rating - 1]
      }
      dataset.borderColor = surfaceColor
    })

    this.chart.options.color = textColor
    for (const scale of Object.values(this.chart.options.scales)) {
      scale.ticks.color = textColor
      scale.grid.color = gridColor
      scale.border = { color: gridColor }
    }

    this.chart.update("none")
  }
}
